import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Users, TrendingUp, DollarSign, Clock, Flame,
  Plus, ArrowRight, ChevronRight,
  AlertCircle, CheckCircle, Activity, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import AttendanceWidget from '../../components/sales/AttendanceWidget';
import { leadsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, sub, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-3.5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-teal-600" />
        </div>
      </div>
    </div>
  );
}

function LeadRowMini({ lead, onClick }) {
  const priorityColors = {
    hot: 'bg-rose-100 text-rose-700',
    warm: 'bg-amber-100 text-amber-700',
    cold: 'bg-sky-100 text-sky-700',
  };
  return (
    <div
      className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
      onClick={() => onClick(lead.id)}
    >
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-teal-500 flex-shrink-0">
        {(lead.full_name || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{lead.full_name}</p>
        <p className="text-xs text-slate-500 truncate">
          {lead.product_interest || lead.visa_type || lead.country_of_interest || '—'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {lead.quoted_price && (
          <span className="text-xs font-semibold text-slate-600">
            INR{Number(lead.quoted_price).toLocaleString()}
          </span>
        )}
        <Badge className={`text-xs px-1.5 ${priorityColors[lead.priority] || priorityColors.warm}`}>
          {lead.priority}
        </Badge>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
      </div>
    </div>
  );
}

export default function SalesDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [overdueLeads, setOverdueLeads] = useState([]);
  const [todayLeads, setTodayLeads] = useState([]);
  const [followUpTab, setFollowUpTab] = useState('overdue');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, leadsRes, overdueRes, todayRes] = await Promise.allSettled([
        leadsAPI.getPipelineAnalytics(),
        leadsAPI.getLeads({ limit: 50 }),
        leadsAPI.getOverdueFollowups(),
        leadsAPI.getTodayFollowups(),
      ]);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      if (leadsRes.status === 'fulfilled') {
        const allLeads = leadsRes.value.data.leads || [];
        setRecentLeads(allLeads.filter((l) => l.stage !== 'registered' && l.stage !== 'not_interested').slice(0, 6));
      }
      if (overdueRes.status === 'fulfilled') setOverdueLeads(overdueRes.value.data.leads || []);
      if (todayRes.status === 'fulfilled') setTodayLeads(todayRes.value.data.leads || []);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sc = analytics?.stage_counts || {};
  const pipelineValue = analytics?.pipeline_value || 0;
  const convRate = analytics?.conversion_rate || 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back, {user?.full_name?.split(' ')[0] || 'there'} 👋
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <Button
            onClick={() => navigate('/sales/leads')}
            className="bg-teal-600 hover:bg-teal-700 gap-1.5"
          >
            <Plus className="w-4 h-4" /> Open Kanban
          </Button>
        </div>

        {/* Attendance Widget */}
        <AttendanceWidget />

        {/* KPI Cards */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array(6).fill(0).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-5 h-24" />
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard
              icon={Users}
              label="New Leads"
              value={sc.new_lead || 0}
              color="bg-blue-500"
              onClick={() => navigate('/sales/leads')}
            />
            <StatCard
              icon={Activity}
              label="In Discussion"
              value={sc.in_discussion || 0}
              color="bg-violet-500"
              onClick={() => navigate('/sales/leads')}
            />
            <StatCard
              icon={Flame}
              label="Prospective"
              value={sc.prospective || 0}
              color="bg-amber-500"
              onClick={() => navigate('/sales/leads')}
            />
            <StatCard
              icon={CheckCircle}
              label="Registered"
              value={sc.registered || 0}
              sub="this period"
              color="bg-teal-500"
            />
            <StatCard
              icon={DollarSign}
              label="Pipeline"
              value={`INR${(pipelineValue / 1000).toFixed(0)}k`}
              sub="active value"
              color="bg-emerald-500"
            />
            <StatCard
              icon={TrendingUp}
              label="Conv. Rate"
              value={`${convRate}%`}
              sub="overall"
              color="bg-indigo-500"
            />
          </div>
        )}

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent leads */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Active Leads</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/sales/leads')}
                    className="gap-1 text-teal-600 text-xs">
                    View all <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {recentLeads.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active leads yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {recentLeads.map((lead) => (
                      <LeadRowMini key={lead.id} lead={lead} onClick={(id) => navigate(`/sales/leads/${id}`)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            {/* Follow-ups widget with tabs */}
            <Card className={overdueLeads.length > 0 ? 'border-rose-200' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-500" /> Follow-ups
                </CardTitle>
                <div className="flex items-center gap-1 mt-2">
                  <button
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${followUpTab === 'overdue' ? 'bg-rose-100 text-rose-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    onClick={() => setFollowUpTab('overdue')}
                  >
                    <AlertCircle className="w-3 h-3" /> Overdue
                    {overdueLeads.length > 0 && <Badge className="bg-rose-200 text-rose-700 ml-1 px-1 py-0 h-4 text-xs">{overdueLeads.length}</Badge>}
                  </button>
                  <button
                    className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${followUpTab === 'today' ? 'bg-sky-100 text-sky-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    onClick={() => setFollowUpTab('today')}
                  >
                    <Calendar className="w-3 h-3" /> Today
                    {todayLeads.length > 0 && <Badge className="bg-sky-200 text-sky-700 ml-1 px-1 py-0 h-4 text-xs">{todayLeads.length}</Badge>}
                  </button>
                </div>
              </CardHeader>
              <CardContent className="pt-1">
                {(() => {
                  const list = followUpTab === 'overdue' ? overdueLeads : todayLeads;
                  const emptyMsg = followUpTab === 'overdue' ? 'All caught up! ✓' : 'No follow-ups due today.';
                  const itemBg = followUpTab === 'overdue' ? 'bg-rose-50 hover:bg-rose-100' : 'bg-sky-50 hover:bg-sky-100';
                  const dateColor = followUpTab === 'overdue' ? 'text-rose-500' : 'text-sky-600';
                  if (list.length === 0) return <p className="text-sm text-slate-400 text-center py-4">{emptyMsg}</p>;
                  return (
                    <div className="space-y-2">
                      {list.slice(0, 5).map((lead) => (
                        <div key={lead.id} className={`flex items-center gap-2 p-2 ${itemBg} rounded-lg cursor-pointer transition-colors`} onClick={() => navigate(`/sales/leads/${lead.id}`)}>
                          <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(lead.full_name || '?')[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{lead.full_name}</p>
                            <p className={`text-xs ${dateColor}`}>
                              {lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                            </p>
                          </div>
                          <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Priority summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Priority Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {[
                  { key: 'hot', label: 'Hot', icon: '🔥', color: 'bg-rose-500', textColor: 'text-rose-700', bg: 'bg-rose-50' },
                  { key: 'warm', label: 'Warm', icon: '🌡', color: 'bg-amber-500', textColor: 'text-amber-700', bg: 'bg-amber-50' },
                  { key: 'cold', label: 'Cold', icon: '❄️', color: 'bg-sky-500', textColor: 'text-sky-700', bg: 'bg-sky-50' },
                ].map(({ key, label, icon, color, textColor, bg }) => {
                  const count = analytics?.priority_breakdown?.[key] || 0;
                  const total = Object.values(analytics?.priority_breakdown || {}).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={key} className={`flex items-center gap-3 p-2.5 rounded-lg ${bg}`}>
                      <span className="text-lg">{icon}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={`font-semibold ${textColor}`}>{label}</span>
                          <span className="text-slate-500">{count} leads</span>
                        </div>
                        <div className="h-1.5 bg-white/80 rounded-full overflow-hidden">
                          <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
